import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Client, LocalStream, type RemoteStream } from 'ion-sdk-js';
import { IonSFUJSONRPCSignal } from 'ion-sdk-js/lib/signal/json-rpc-impl';
import type { TeleattendanceVideoConnection } from '../../services/teleattendanceService';
import type { VideoLabels } from './types';
import DraggableFloatingPreview from './DraggableFloatingPreview';
import VideoFrame from './VideoFrame';
import VideoStage from './VideoStage';

const IonSFUVideoConference = ({
  connection,
  videoLabels,
}: {
  connection: TeleattendanceVideoConnection;
  videoLabels: VideoLabels;
}) => {
  const { t } = useTranslation();
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState('');
  const [isConnecting, setIsConnecting] = useState(true);

  useEffect(() => {
    if (
      !connection.serverUrl ||
      !connection.roomName ||
      !connection.participantIdentity
    ) {
      setError(
        t('teleattendance.video_data_incomplete') ||
          'Os dados de conexão da videoconferência estão incompletos.',
      );
      setIsConnecting(false);
      return;
    }

    let isMounted = true;
    let client: Client | null = null;
    let signal: IonSFUJSONRPCSignal | null = null;
    let localStream: LocalStream | null = null;

    const stopLocalStream = () => {
      if (!localStream) return;
      localStream.getTracks().forEach((track) => track.stop());
      localStream = null;
    };

    const cleanupVideoElement = (element: HTMLVideoElement | null) => {
      if (!element) return;
      element.pause();
      element.srcObject = null;
    };

    const connect = async () => {
      try {
        signal = new IonSFUJSONRPCSignal(connection.serverUrl);
        client = new Client(signal);

        client.ontrack = (_track: MediaStreamTrack, stream: RemoteStream) => {
          if (!isMounted || !remoteVideoRef.current) return;
          remoteVideoRef.current.srcObject = stream;
          void remoteVideoRef.current.play().catch(() => undefined);
        };

        signal.onerror = () => {
          if (!isMounted) return;
          setError(
            t('teleattendance.video_connect_server_error') ||
              'Não foi possível conectar ao servidor de vídeo.',
          );
          setIsConnecting(false);
        };

        signal.onclose = () => {
          if (!isMounted) return;
          setIsConnecting(false);
        };

        signal.onopen = async () => {
          try {
            localStream = await LocalStream.getUserMedia({
              audio: true,
              video: true,
              resolution: 'hd',
              codec: 'vp8',
              simulcast: false,
            } as any);

            if (localVideoRef.current) {
              localVideoRef.current.srcObject = localStream;
              localVideoRef.current.muted = true;
              void localVideoRef.current.play().catch(() => undefined);
            }

            await client?.join(
              connection.roomName,
              connection.participantIdentity,
            );
            if (localStream && client) {
              client.publish(localStream);
            }

            if (!isMounted) return;
            setError('');
            setIsConnecting(false);
          } catch (joinError) {
            stopLocalStream();
            if (!isMounted) return;
            setError(
              t('teleattendance.video_start_ionsfu_error') ||
                'Não foi possível iniciar a chamada por ion-sfu.',
            );
            setIsConnecting(false);
            console.error(joinError);
          }
        };
      } catch (connectionError) {
        if (!isMounted) return;
        setError(
          t('teleattendance.video_prepare_error') ||
            'Não foi possível preparar a conexão de vídeo.',
        );
        setIsConnecting(false);
        console.error(connectionError);
      }
    };

    void connect();

    return () => {
      isMounted = false;
      stopLocalStream();
      cleanupVideoElement(localVideoRef.current);
      cleanupVideoElement(remoteVideoRef.current);
      client?.close();
      signal?.close();
    };
  }, [
    connection.participantIdentity,
    connection.roomName,
    connection.serverUrl,
    t,
  ]);

  return (
    <div className="relative min-h-[60vh] rounded-[28px] border border-[#cfe2ff] bg-[linear-gradient(180deg,_rgba(255,255,255,0.98),_rgba(248,250,252,0.98))] p-4 shadow-[0_24px_60px_rgba(59,130,246,0.12)] sm:p-5">
      <VideoStage label={videoLabels.main} className="h-full">
        <VideoFrame className="h-[58vh] min-h-[320px]">
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="h-full w-full bg-slate-200 object-cover"
          />
        </VideoFrame>
      </VideoStage>

      <DraggableFloatingPreview
        className="w-[38vw] max-w-[240px] min-w-[140px]"
        defaultPlacementClassName="bottom-8 right-8"
      >
        <VideoStage label={videoLabels.preview} hideLabel>
          <VideoFrame className="aspect-[4/3]">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className="h-full w-full bg-slate-200 object-cover"
            />
          </VideoFrame>
        </VideoStage>
      </DraggableFloatingPreview>

      {isConnecting ? (
        <div className="absolute left-6 top-6 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700 shadow-sm">
          {t('teleattendance.connecting') || 'Conectando...'}
        </div>
      ) : null}

      {error ? (
        <div className="absolute inset-x-4 bottom-4 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive sm:inset-x-6">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default IonSFUVideoConference;
